import { createId } from '../lib/ids';
import { OpError, songBars } from './projectOps';
import {
  AUDIO_LANES,
  DRUM_INSTRUMENTS,
  MAX_AUDIO_CLIPS,
  MAX_CLIP_LOOPS,
  MAX_FADE_SECONDS,
  MAX_SONG_BARS,
  STEPS_PER_BAR,
  type Asset,
  type AssetAudioInfo,
  type AudioBus,
  type AudioClip,
  type Instrument,
  type Project,
  type SampleSlot,
  type Track,
} from './schemas';

/**
 * Pure operations for sample instruments and audio clips (Phase 2). Like projectOps,
 * each returns a new Project or throws OpError with a user-facing message and
 * changes nothing.
 *
 * Timebase: a clip's position is in 16th-note steps from the start of the song (the
 * arrangement's grid), its contents are in seconds of source audio. A clip's length
 * on the timeline therefore depends on the tempo; overlap checks use the current BPM.
 */

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));
const round3 = (v: number) => Math.round(v * 1000) / 1000;
const MAX_STEP = MAX_SONG_BARS * STEPS_PER_BAR;

export const stepSeconds = (bpm: number) => 60 / bpm / 4;

/** Seconds of timeline one clip occupies, including loops. */
export const clipTimelineSeconds = (clip: Pick<AudioClip, 'durationSec' | 'playbackRate' | 'loops'>) =>
  (clip.durationSec / clip.playbackRate) * clip.loops;

export const clipLengthSteps = (clip: AudioClip, bpm: number) => clipTimelineSeconds(clip) / stepSeconds(bpm);
export const clipEndStep = (clip: AudioClip, bpm: number) => clip.startStep + clipLengthSteps(clip, bpm);

/** Snap grids offered by the UI, in steps (0 = free placement to 1/100 step). */
export const SNAP_GRIDS = { off: 0, step: 1, beat: 4, bar: 16 } as const;
export type SnapGrid = keyof typeof SNAP_GRIDS;

export function snapStep(step: number, grid: SnapGrid): number {
  const size = SNAP_GRIDS[grid];
  const snapped = size === 0 ? Math.round(step * 100) / 100 : Math.round(step / size) * size;
  return clamp(snapped, 0, MAX_STEP);
}

/** Step at which an arrangement section begins. */
export function sectionStartStep(project: Project, sectionId: string): number {
  let bars = 0;
  for (const section of project.arrangement.sections) {
    if (section.id === sectionId) return bars * STEPS_PER_BAR;
    bars += section.bars;
  }
  throw new OpError('That section no longer exists.');
}

export const songLengthSteps = (project: Project) => songBars(project) * STEPS_PER_BAR;

// ---------------------------------------------------------------- Assets

export function audioAsset(project: Project, assetId: string): Asset {
  const asset = project.assets.find((a) => a.id === assetId);
  if (!asset) throw new OpError('That audio file is no longer in the library.');
  if (asset.kind !== 'audio') throw new OpError(`“${asset.name}” is not an audio file.`);
  return asset;
}

function knownAudio(asset: Asset): AssetAudioInfo {
  if (!asset.audio) throw new OpError(`“${asset.name}” has not been analysed yet. Preview it once, then try again.`);
  return asset.audio;
}

export function setAssetAudioInfo(project: Project, assetId: string, audio: AssetAudioInfo): Project {
  audioAsset(project, assetId);
  return { ...project, assets: project.assets.map((a) => (a.id === assetId ? { ...a, audio } : a)) };
}

export function renameAsset(project: Project, assetId: string, name: string): Project {
  const trimmed = name.trim().slice(0, 200);
  if (!trimmed) throw new OpError('A name cannot be empty.');
  audioAsset(project, assetId);
  return { ...project, assets: project.assets.map((a) => (a.id === assetId ? { ...a, name: trimmed } : a)) };
}

export interface AssetUsage {
  instruments: Instrument[];
  clipIds: string[];
}

/** Where an asset is used in the project (sample slots and clips). */
export function assetUsage(project: Project, assetId: string): AssetUsage {
  return {
    instruments: project.tracks.filter((t) => t.sample?.assetId === assetId).map((t) => t.instrument),
    clipIds: project.audioClips.filter((c) => c.assetId === assetId).map((c) => c.id),
  };
}

export const isAssetUsed = (project: Project, assetId: string) => {
  const usage = assetUsage(project, assetId);
  return usage.instruments.length > 0 || usage.clipIds.length > 0;
};

/** Removes every reference to an asset (used when the user confirms deleting a used file). */
export function detachAsset(project: Project, assetId: string): Project {
  return {
    ...project,
    tracks: project.tracks.map((t) => (t.sample?.assetId === assetId ? { ...t, sample: null } : t)),
    audioClips: project.audioClips.filter((c) => c.assetId !== assetId),
  };
}

export const projectAudioBytes = (project: Project) =>
  project.assets.filter((a) => a.kind === 'audio').reduce((sum, a) => sum + a.sizeBytes, 0);

// ---------------------------------------------------------------- Sample instruments

export const isSampleable = (instrument: Instrument) => DRUM_INSTRUMENTS.includes(instrument);

function drumTrack(project: Project, instrument: Instrument): Track {
  if (!isSampleable(instrument)) {
    throw new OpError('Samples can only be assigned to drum and percussion tracks (kick, snare, clap, hats, perc).');
  }
  const track = project.tracks.find((t) => t.instrument === instrument);
  if (!track) throw new OpError(`This project has no ${instrument} track.`);
  return track;
}

const withTrack = (project: Project, instrument: Instrument, patch: Partial<Track>): Project => ({
  ...project,
  tracks: project.tracks.map((t) => (t.instrument === instrument ? { ...t, ...patch } : t)),
});

export const defaultSampleSlot = (assetId: string): SampleSlot => ({
  assetId,
  gainDb: 0,
  pitchSemitones: 0,
  trimStartSec: 0,
  trimEndSec: null,
  reverse: false,
  chokeGroup: 0,
});

/**
 * Plays the asset instead of the synthesized drum. The track's notes are untouched.
 * Replacing a sample keeps gain, pitch and choke settings; trims reset because they
 * belonged to the previous file.
 */
export function assignSample(project: Project, instrument: Instrument, assetId: string): Project {
  const track = drumTrack(project, instrument);
  audioAsset(project, assetId);
  const slot = track.sample
    ? { ...track.sample, assetId, trimStartSec: 0, trimEndSec: null }
    : defaultSampleSlot(assetId);
  return withTrack(project, instrument, { sample: slot });
}

export function unassignSample(project: Project, instrument: Instrument): Project {
  const track = drumTrack(project, instrument);
  if (!track.sample) return project;
  return withTrack(project, instrument, { sample: null });
}

export function updateSampleSlot(project: Project, instrument: Instrument, patch: Partial<Omit<SampleSlot, 'assetId'>>): Project {
  const track = drumTrack(project, instrument);
  if (!track.sample) throw new OpError(`The ${track.name} track uses its built-in sound; assign a sample first.`);
  const duration = project.assets.find((a) => a.id === track.sample!.assetId)?.audio?.durationSec ?? Infinity;
  const next = { ...track.sample, ...patch };
  next.gainDb = round3(clamp(next.gainDb, -24, 12));
  next.pitchSemitones = Math.round(clamp(next.pitchSemitones, -24, 24));
  next.chokeGroup = Math.round(clamp(next.chokeGroup, 0, 4));
  next.trimStartSec = round3(clamp(next.trimStartSec, 0, Math.min(duration, 180)));
  if (next.trimEndSec !== null) {
    next.trimEndSec = round3(clamp(next.trimEndSec, 0, Math.min(duration, 180)));
    if (next.trimEndSec <= next.trimStartSec) throw new OpError('The trim end must be after the trim start.');
  }
  return withTrack(project, instrument, { sample: next });
}

// ---------------------------------------------------------------- Clips

function clipIndex(project: Project, clipId: string): number {
  const index = project.audioClips.findIndex((c) => c.id === clipId);
  if (index < 0) throw new OpError('That clip no longer exists.');
  return index;
}

export const clipOf = (project: Project, clipId: string): AudioClip => project.audioClips[clipIndex(project, clipId)]!;

/** First clip on the same lane that would overlap `clip`, if any. */
export function findOverlap(project: Project, clip: AudioClip, ignoreId = clip.id): AudioClip | undefined {
  const bpm = project.settings.bpm;
  const start = clip.startStep;
  const end = clipEndStep(clip, bpm);
  const EPS = 1e-6;
  return project.audioClips.find(
    (c) => c.id !== ignoreId && c.lane === clip.lane && c.startStep < end - EPS && clipEndStep(c, bpm) > start + EPS,
  );
}

function checkPlacement(project: Project, clip: AudioClip): void {
  if (clipEndStep(clip, project.settings.bpm) > MAX_STEP + 1e-6) {
    throw new OpError(`The clip would end after bar ${MAX_SONG_BARS}, the longest song allowed.`);
  }
  const other = findOverlap(project, clip);
  if (other) {
    throw new OpError(
      `“${clip.name}” would overlap “${other.name}” on lane ${clip.lane + 1}. Move it, use another lane, or shorten one of them.`,
    );
  }
}

/** First lane where the clip fits without overlapping, or undefined when all are taken. */
export function freeLane(project: Project, clip: AudioClip, preferred = clip.lane): number | undefined {
  const order = [preferred, ...Array.from({ length: AUDIO_LANES }, (_, i) => i).filter((l) => l !== preferred)];
  return order.find((lane) => !findOverlap(project, { ...clip, lane }));
}

function replaceClip(project: Project, clip: AudioClip): Project {
  checkPlacement(project, clip);
  return { ...project, audioClips: project.audioClips.map((c) => (c.id === clip.id ? clip : c)) };
}

export interface AddClipOptions {
  startStep: number;
  /** Omit to use the first free lane. */
  lane?: number;
  id?: string;
}

export function addClip(project: Project, assetId: string, options: AddClipOptions): Project {
  if (project.audioClips.length >= MAX_AUDIO_CLIPS) throw new OpError(`A project can hold at most ${MAX_AUDIO_CLIPS} clips.`);
  const asset = audioAsset(project, assetId);
  const audio = knownAudio(asset);
  const base: AudioClip = {
    id: options.id ?? createId('clp'),
    name: asset.name.replace(/\.[a-z0-9]{2,4}$/i, '').slice(0, 120) || 'Clip',
    assetId,
    lane: clamp(Math.round(options.lane ?? 0), 0, AUDIO_LANES - 1),
    startStep: clamp(options.startStep, 0, MAX_STEP),
    offsetSec: 0,
    durationSec: round3(Math.max(0.01, Math.min(audio.durationSec, 180))),
    gainDb: 0,
    muted: false,
    fadeInSec: 0,
    fadeOutSec: 0,
    playbackRate: 1,
    reverse: false,
    normalize: false,
    loops: 1,
    filter: { type: 'off', frequencyHz: 1000 },
  };
  let lane = base.lane;
  if (options.lane === undefined) {
    const free = freeLane(project, base, 0);
    if (free === undefined) throw new OpError('Every lane is busy at that position. Pick a different spot.');
    lane = free;
  }
  const clip = { ...base, lane };
  checkPlacement(project, clip);
  return { ...project, audioClips: [...project.audioClips, clip] };
}

export function moveClip(project: Project, clipId: string, to: { startStep?: number; lane?: number }): Project {
  const clip = clipOf(project, clipId);
  const moved = {
    ...clip,
    startStep: clamp(to.startStep ?? clip.startStep, 0, MAX_STEP),
    lane: clamp(Math.round(to.lane ?? clip.lane), 0, AUDIO_LANES - 1),
  };
  if (moved.startStep === clip.startStep && moved.lane === clip.lane) return project;
  return replaceClip(project, moved);
}

/** Copies the clip directly after itself (same lane if free, otherwise the first free lane). */
export function duplicateClip(project: Project, clipId: string, newId = createId('clp')): Project {
  if (project.audioClips.length >= MAX_AUDIO_CLIPS) throw new OpError(`A project can hold at most ${MAX_AUDIO_CLIPS} clips.`);
  const clip = clipOf(project, clipId);
  const copy: AudioClip = {
    ...structuredClone(clip),
    id: newId,
    name: `${clip.name} copy`.slice(0, 120),
    startStep: round3(clipEndStep(clip, project.settings.bpm)),
  };
  const lane = freeLane(project, copy);
  if (lane === undefined) throw new OpError('There is no free lane right after this clip for the copy.');
  const placed = { ...copy, lane };
  checkPlacement(project, placed);
  const index = clipIndex(project, clipId);
  const audioClips = [...project.audioClips];
  audioClips.splice(index + 1, 0, placed);
  return { ...project, audioClips };
}

export function removeClip(project: Project, clipId: string): Project {
  clipIndex(project, clipId);
  return { ...project, audioClips: project.audioClips.filter((c) => c.id !== clipId) };
}

export type ClipPatch = Partial<Omit<AudioClip, 'id' | 'assetId'>>;

/**
 * Updates clip properties with clamping. Trim changes keep the clip inside its source
 * audio; fades shrink when the clip gets too short for them. Placement changes are
 * checked for overlaps.
 */
export function updateClip(project: Project, clipId: string, patch: ClipPatch): Project {
  const clip = clipOf(project, clipId);
  const asset = audioAsset(project, clip.assetId);
  const sourceSec = asset.audio?.durationSec ?? clip.offsetSec + clip.durationSec;
  const next: AudioClip = { ...clip, ...patch, filter: { ...clip.filter, ...patch.filter } };
  if (patch.name !== undefined) {
    next.name = patch.name.trim().slice(0, 120);
    if (!next.name) throw new OpError('A clip name cannot be empty.');
  }
  next.lane = clamp(Math.round(next.lane), 0, AUDIO_LANES - 1);
  next.startStep = clamp(next.startStep, 0, MAX_STEP);
  next.gainDb = round3(clamp(next.gainDb, -48, 12));
  next.playbackRate = round3(clamp(next.playbackRate, 0.25, 4));
  next.loops = Math.round(clamp(next.loops, 1, MAX_CLIP_LOOPS));
  next.filter.frequencyHz = Math.round(clamp(next.filter.frequencyHz, 20, 20000));
  next.offsetSec = round3(clamp(next.offsetSec, 0, Math.max(0, sourceSec - 0.01)));
  next.durationSec = round3(clamp(next.durationSec, 0.01, sourceSec - next.offsetSec));
  const playedSec = next.durationSec / next.playbackRate;
  next.fadeInSec = round3(clamp(next.fadeInSec, 0, Math.min(MAX_FADE_SECONDS, playedSec)));
  next.fadeOutSec = round3(clamp(next.fadeOutSec, 0, Math.min(MAX_FADE_SECONDS, playedSec - next.fadeInSec)));
  return replaceClip(project, next);
}

/** Moves the clip's left or right edge, keeping the audio under the right edge in place. */
export function trimClip(project: Project, clipId: string, edge: 'start' | 'end', deltaSec: number): Project {
  const clip = clipOf(project, clipId);
  if (edge === 'end') return updateClip(project, clipId, { durationSec: clip.durationSec + deltaSec });
  const asset = audioAsset(project, clip.assetId);
  const sourceSec = asset.audio?.durationSec ?? clip.offsetSec + clip.durationSec;
  const offset = clamp(clip.offsetSec + deltaSec, 0, clip.offsetSec + clip.durationSec - 0.01);
  const applied = offset - clip.offsetSec;
  const startStep = clip.startStep + applied / clip.playbackRate / stepSeconds(project.settings.bpm);
  if (startStep < 0) throw new OpError('The clip already starts at the beginning of the song.');
  return updateClip(project, clipId, {
    offsetSec: offset,
    durationSec: Math.min(clip.durationSec - applied, sourceSec - offset),
    startStep,
  });
}

export const setClipGain = (p: Project, id: string, gainDb: number) => updateClip(p, id, { gainDb });
export const adjustClipGain = (p: Project, id: string, deltaDb: number) => updateClip(p, id, { gainDb: clipOf(p, id).gainDb + deltaDb });

// ---------------------------------------------------------------- Audio bus

export function updateAudioBus(project: Project, patch: Partial<AudioBus>): Project {
  const next = { ...project.audioBus, ...patch };
  next.volumeDb = round3(clamp(next.volumeDb, -60, 6));
  next.pan = round3(clamp(next.pan, -1, 1));
  next.delaySend = round3(clamp(next.delaySend, 0, 1));
  next.reverbSend = round3(clamp(next.reverbSend, 0, 1));
  return { ...project, audioBus: next };
}
